import { useEffect, useMemo, useState } from "react";
import { api, fmtINR, fmtPct, fmtDate, todayISO, firstOfMonthISO } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFilters } from "@/context/FiltersContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Loader2 } from "lucide-react";

const emptyLog = {
  date: todayISO(),
  executive_id: "",
  revenue: 0, new_partners: 0, partner_visits: 0, active_partners: 0,
  referrals_generated: 0, referrals_converted: 0, crm_entries: 0, disputes: 0,
  report_submitted: false, event_organized: false, partners_at_event: 0, remarks: "",
};

export default function DailyLog() {
  const { user } = useAuth();
  const { executives, selectedExecId, dateFrom, setDateFrom, dateTo, setDateTo } = useFilters();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyLog });
  const [saving, setSaving] = useState(false);

  const params = useMemo(() => {
    const p = { date_from: dateFrom, date_to: dateTo };
    if (user?.role !== "executive" && selectedExecId !== "all") p.executive_id = selectedExecId;
    return p;
  }, [user, selectedExecId, dateFrom, dateTo]);

  const load = () => {
    setLoading(true);
    api.get("/daily-logs", { params }).then((r) => setLogs(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [params]);

  const openNew = () => {
    setForm({
      ...emptyLog,
      executive_id: user.role === "executive" ? user.executive_id : (executives[0]?.id || ""),
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.executive_id) { toast.error("Choose executive"); return; }
    setSaving(true);
    try {
      await api.post("/daily-logs", { ...form, revenue: Number(form.revenue) });
      toast.success("Log added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const totals = logs.reduce((acc, l) => {
    acc.rev += l.revenue; acc.visits += l.partner_visits; acc.gen += l.referrals_generated;
    acc.conv += l.referrals_converted; acc.new += l.new_partners; return acc;
  }, { rev: 0, visits: 0, gen: 0, conv: 0, new: 0 });
  const avgDisc = logs.length ? (logs.reduce((s, l) => s + l.discipline_score, 0) / logs.length).toFixed(2) : "0";
  const convPct = totals.gen ? ((totals.conv / totals.gen) * 100).toFixed(1) : "0";

  const execName = (id) => executives.find((e) => e.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="daily-log-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#C9A24B]">Field Operations</div>
          <h2 className="text-3xl font-light text-white mt-1" style={{ fontFamily: "Outfit" }}>
            Daily Activity &amp; Revenue Log
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-slate-800/60 border border-slate-700 rounded-lg overflow-hidden">
            <input data-testid="log-date-from" type="date" value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="bg-transparent text-white px-3 py-1.5 text-sm outline-none" />
            <span className="text-slate-500 px-2">→</span>
            <input data-testid="log-date-to" type="date" value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="bg-transparent text-white px-3 py-1.5 text-sm outline-none" />
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="btn-add-log" onClick={openNew}
                className="bg-[#C9A24B] hover:bg-[#b08d40] text-[#0F172A] font-semibold rounded-lg px-4 py-2 text-sm flex items-center gap-2">
                <Plus size={16} /> Add Log
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#0F172A] border-slate-700 text-white max-w-2xl">
              <DialogHeader><DialogTitle>New Daily Log</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                {user?.role !== "executive" && (
                  <div className="col-span-2">
                    <label className="text-xs text-slate-400">Executive</label>
                    <Select value={form.executive_id} onValueChange={(v) => setForm({ ...form, executive_id: v })}>
                      <SelectTrigger className="bg-slate-900 border-slate-700 mt-1" data-testid="log-exec-select">
                        <SelectValue placeholder="Choose executive" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0F172A] border-slate-700 text-white">
                        {executives.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {[
                  ["date", "Date", "date"],
                  ["revenue", "Revenue (₹)", "number"],
                  ["new_partners", "New Partners", "number"],
                  ["partner_visits", "Partner Visits", "number"],
                  ["active_partners", "Total Active Partners", "number"],
                  ["referrals_generated", "Referrals Generated", "number"],
                  ["referrals_converted", "Referrals Converted (Paid)", "number"],
                  ["crm_entries", "CRM Entries", "number"],
                  ["disputes", "Commission Disputes", "number"],
                  ["partners_at_event", "Partners at Event", "number"],
                ].map(([k, label, type]) => (
                  <div key={k}>
                    <label className="text-xs text-slate-400">{label}</label>
                    <input
                      data-testid={`log-input-${k}`}
                      type={type}
                      value={form[k]}
                      onChange={(e) => setForm({ ...form, [k]: type === "number" ? Number(e.target.value) : e.target.value })}
                      className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#C9A24B]"
                    />
                  </div>
                ))}
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid="log-report" checked={form.report_submitted}
                    onChange={(e) => setForm({ ...form, report_submitted: e.target.checked })} />
                  Report submitted
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid="log-event" checked={form.event_organized}
                    onChange={(e) => setForm({ ...form, event_organized: e.target.checked })} />
                  Event/Camp organized
                </label>
                <div className="col-span-2">
                  <label className="text-xs text-slate-400">Remarks</label>
                  <textarea data-testid="log-remarks" value={form.remarks}
                    onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                    className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#C9A24B]"
                    rows={2} />
                </div>
              </div>
              <DialogFooter>
                <button data-testid="log-save" onClick={submit} disabled={saving}
                  className="bg-[#C9A24B] hover:bg-[#b08d40] text-[#0F172A] font-semibold rounded-lg px-4 py-2 flex items-center gap-2">
                  {saving && <Loader2 size={14} className="animate-spin" />} Save
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          ["Revenue", fmtINR(totals.rev)],
          ["Visits", totals.visits],
          ["New Partners", totals.new],
          ["Referrals Gen", totals.gen],
          ["Referrals Conv", `${totals.conv} (${convPct}%)`],
          ["Avg Discipline", `${avgDisc}/10`],
        ].map(([label, value]) => (
          <div key={label} className="bg-[#1E293B] border border-slate-800 rounded-xl p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
            <div className="text-white text-lg font-medium mt-1" style={{ fontFamily: "Outfit" }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Log table */}
      <div className="bg-[#1E293B] rounded-xl border border-slate-800 overflow-hidden" data-testid="log-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-800/50 text-slate-300 text-[11px] uppercase tracking-wider">
                {["Date", "Executive", "Revenue", "New Partners", "Visits", "Active", "Coverage", "Ref Gen", "Ref Conv", "Conv %", "CRM", "Disp", "Rpt", "Evt", "Score"].map((h) => (
                  <th key={h} className="text-left px-3 py-3 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={15} className="p-8 text-center text-slate-400">Loading…</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={15} className="p-8 text-center text-slate-400">No logs in this range.</td></tr>
              ) : logs.map((l) => (
                <tr key={l.id} className="border-t border-slate-800 hover:bg-slate-800/30 transition-colors">
                  <td className="px-3 py-2.5 text-slate-200">{fmtDate(l.date)}</td>
                  <td className="px-3 py-2.5 text-slate-200">{execName(l.executive_id)}</td>
                  <td className="px-3 py-2.5 text-emerald-400 font-medium">{fmtINR(l.revenue)}</td>
                  <td className="px-3 py-2.5 text-slate-200">{l.new_partners}</td>
                  <td className="px-3 py-2.5 text-slate-200">{l.partner_visits}</td>
                  <td className="px-3 py-2.5 text-slate-200">{l.active_partners}</td>
                  <td className={`px-3 py-2.5 font-medium ${l.visit_coverage_pct >= 80 ? "text-emerald-400" : l.visit_coverage_pct >= 60 ? "text-amber-400" : "text-rose-400"}`}>
                    {fmtPct(l.visit_coverage_pct)}
                  </td>
                  <td className="px-3 py-2.5 text-slate-200">{l.referrals_generated}</td>
                  <td className="px-3 py-2.5 text-slate-200">{l.referrals_converted}</td>
                  <td className="px-3 py-2.5 text-slate-200">{fmtPct(l.referral_conv_pct)}</td>
                  <td className="px-3 py-2.5 text-slate-200">{l.crm_entries}</td>
                  <td className={`px-3 py-2.5 ${l.disputes > 0 ? "text-rose-400 font-semibold" : "text-slate-200"}`}>{l.disputes}</td>
                  <td className="px-3 py-2.5">{l.report_submitted ? "✓" : "—"}</td>
                  <td className="px-3 py-2.5">{l.event_organized ? "✓" : "—"}</td>
                  <td className={`px-3 py-2.5 font-semibold ${l.discipline_score >= 8 ? "text-emerald-400" : l.discipline_score >= 6 ? "text-amber-400" : "text-rose-400"}`}>
                    {l.discipline_score}/10
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
